import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { NotificationsTable } from "@/components/notifications-center/NotificationsTable";
import { NotificationFilters, NotificationFiltersData } from "@/components/notifications-center/NotificationsFilters";
import { RefreshCw, LayoutDashboard, Hospital } from "lucide-react";
import { toast } from "sonner";
import { fetchNotifications, resendNotification } from "@/services/notification-center-service";
import { useClinic } from "@/contexts/ClinicContext";

const StatBadge = ({ label, value, color }: { label: string; value: number; color: string }) => (
  <div className="flex items-center gap-1.5 bg-card border border-border rounded-md px-3 py-1.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className={`text-sm font-bold ${color}`}>{value}</span>
  </div>
);

export default function Notifications() {
  const { selectedClinicId, selectedClinic } = useClinic();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const today = new Date().toISOString().split("T")[0];
  const [filters, setFilters] = useState<NotificationFiltersData>({
    pageSize: 20,
    startDate: today,
    endDate: today,
  });

  const loadNotifications = async (activeFilters: NotificationFiltersData, page: number) => {
    if (!selectedClinicId) {
      toast.error("Selecione uma clínica no topo da tela para carregar as notificações.");
      return;
    }
    setIsLoading(true);
    try {
      const data = await fetchNotifications({
        ...activeFilters,
        clinicId: selectedClinicId,
        page,
      });
      setNotifications(data);
    } catch (error) {
      toast.error("Erro ao carregar notificações");
    } finally {
      setIsLoading(false);
    }
  };

  // Limpa o grid imediatamente ao trocar de clínica — sem chamar o backend
  useEffect(() => {
    setNotifications([]);
    setCurrentPage(1);
  }, [selectedClinicId]);

  const handleResend = async (id: string) => {
    try {
      await resendNotification(id);
      toast.success("Notificação colocada na fila de reenvio!");
      loadNotifications(filters, currentPage);
    } catch (error) {
      toast.error("Erro ao processar reenvio");
    }
  };

  const stats = {
    total: notifications.length,
    review: notifications.filter(n => n.process_status === "REVIEW").length,
    pending: notifications.filter(n => n.process_status === "PENDING").length,
    sent: notifications.filter(n => ["SENDING", "SENT"].includes(n.process_status)).length,
    delivered: notifications.filter(n => ["DELIVERED", "READ", "RESPONDED", "COMPLETE"].includes(n.process_status)).length,
    failed: notifications.filter(n => ["ERROR", "ABORTED"].includes(n.process_status)).length,
  };

  return (
    <div className="page-wrapper">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <LayoutDashboard className="page-icon" />
            <h1 className="page-title">Central de Notificações</h1>
            {selectedClinic && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-cyan-600 text-white shadow-sm">
                <Hospital className="h-3.5 w-3.5" />
                {selectedClinic.trade_name}
              </span>
            )}
          </div>
          <p className="page-subtitle">
            Acompanhe os envios automáticos e gerencie erros de processamento.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => loadNotifications(filters, currentPage)} disabled={isLoading} className="gap-1.5 h-7 text-xs">
            <RefreshCw className={`h-3 w-3 ${isLoading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Resumo compacto */}
      <div className="flex flex-wrap gap-2">
        <StatBadge label="Total" value={stats.total} color="text-foreground" />
        <StatBadge label="Em Revisão" value={stats.review} color="text-orange-600" />
        <StatBadge label="Pendentes" value={stats.pending} color="text-yellow-600" />
        <StatBadge label="Enviadas" value={stats.sent} color="text-cyan-600" />
        <StatBadge label="Entregues" value={stats.delivered} color="text-green-600" />
        <StatBadge label="Falhas" value={stats.failed} color="text-red-600" />
      </div>

      <NotificationFilters
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setCurrentPage(1);
          loadNotifications(newFilters, 1);
        }}
      />

      <div className="space-y-2">
        <p className="page-counter">
          {notifications.length} registros nesta página
        </p>
        <NotificationsTable
          notifications={notifications}
          onResend={handleResend}
          isLoading={isLoading}
          onChanged={() => loadNotifications(filters, currentPage)}
        />
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
          <span className="page-counter">Página {currentPage}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="btn-header" disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
            <Button variant="outline" size="sm" className="btn-header"
              onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
          </div>
        </div>
      </div>
    </div>
  );
}